import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { queryClient } from "@/lib/queryClient";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import {
  Edit,
  Trash2,
  PlusCircle,
  RefreshCw,
  Check,
  X,
  Coins,
  Gem,
  Badge,
  Palette,
  Layers,
  Package,
  Crown,
  Shirt,
  LayoutDashboard,
} from "lucide-react";
import { ShopItemDialog } from "@/components/ui/shop-item-dialog";
import { ImageUploadField } from "@/components/ui/image-upload-field";
import { apiRequest } from "@/lib/api";

// Schema for shop item form data
const shopItemFormSchema = z.object({
  name: z
    .string()
    .min(1, "Item name is required")
    .max(50, "Item name too long"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(200, "Description too long"),
  imageUrl: z.string().optional(),
  price: z.number().int().positive("Price must be a positive number"),
  currencyType: z.enum(["regular", "premium"]),
  itemType: z.string().min(1, "Item type is required"),
  category: z.string().min(1, "Category is required"),
  isActive: z.boolean(),
  managerMode: z.boolean(),
  stock: z.number().int().nonnegative().nullable(),
});

type ShopItemFormData = z.infer<typeof shopItemFormSchema>;

// Define a type for the shop item data
type ShopItem = {
  id: number;
  name: string;
  description: string;
  imageUrl?: string | null;
  price: number;
  currencyType: "regular" | "premium";
  itemType: string;
  category: string;
  isActive: boolean;
  managerMode: boolean;
  stock: number | null;
};

export function ShopItemsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);
  const [formData, setFormData] = useState<ShopItemFormData>({
    name: "",
    description: "",
    imageUrl: "",
    price: 100,
    currencyType: "regular",
    itemType: "avatar",
    category: "decoration",
    isActive: true,
    managerMode: false,
    stock: null,
  });

  // Query to get all shop items (using the /shop endpoint from currency/shop.ts)
  const {
    data: shopItems,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["/api/currency/shop/all"],
    queryFn: () => apiRequest<ShopItem[]>("/api/currency/shop/all"),
  });

  // Mutation to add a new shop item
  const addItemMutation = useMutation({
    mutationFn: async (data: ShopItemFormData) => {
      return apiRequest<ShopItem>("/api/currency/shop", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/all"] });
      toast({
        title: "Item added successfully",
        description: "The new shop item has been added to the system.",
      });
      setIsAddDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding item",
        description:
          error.message ?? "Failed to add the item. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to update a shop item
  const updateItemMutation = useMutation({
    mutationFn: async (data: { id: number; item: ShopItemFormData }) => {
      return apiRequest<ShopItem>(`/api/currency/shop/${data.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.item),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop/all"] });
      toast({
        title: "Item updated successfully",
        description: "The shop item has been updated.",
      });
      setIsEditDialogOpen(false);
      setSelectedItem(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating item",
        description:
          error.message ?? "Failed to update the item. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to delete a shop item
  const deleteItemMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest<void>(`/api/currency/shop/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency/shop"] });
      toast({
        title: "Item deleted successfully",
        description: "The shop item has been removed from the system.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting item",
        description:
          error.message ?? "Failed to delete the item. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAddSubmit = (data: ShopItemFormData) => {
    try {
      const validatedData = shopItemFormSchema.parse(data);
      addItemMutation.mutate(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleEditSubmit = (data: ShopItemFormData) => {
    if (!selectedItem) return;

    try {
      const validatedData = shopItemFormSchema.parse(data);
      updateItemMutation.mutate({ id: selectedItem.id, item: validatedData });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    deleteItemMutation.mutate(id);
  };

  const openEditDialog = (item: ShopItem) => {
    setSelectedItem(item);
    // Set form data based on the selected item for the dialog
    // Note: This assumes the ShopItemDialog handles its own form state based on initialData
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    // Reset state for the *add* dialog, if it were managed here
    // Since ShopItemDialog manages its state, this might not be needed
    // or should reset any state specific to the parent component
    setFormData({
      name: "",
      description: "",
      imageUrl: "",
      price: 100,
      currencyType: "regular",
      itemType: "avatar",
      category: "decoration",
      isActive: true,
      managerMode: false,
      stock: null,
    });
  };

  const getItemTypeIcon = (type: string) => {
    switch (type) {
      case "avatar":
        return <Badge className="h-4 w-4 text-blue-500" />;
      case "banner":
        return <Palette className="h-4 w-4 text-purple-500" />;
      case "board":
        return <LayoutDashboard className="h-4 w-4 text-cyan-500" />;
      case "cardDesign":
        return <Layers className="h-4 w-4 text-amber-500" />;
      case "booster":
        return <Package className="h-4 w-4 text-green-500" />;
      case "theme":
        return <Palette className="h-4 w-4 text-indigo-500" />;
      case "special":
        return <Crown className="h-4 w-4 text-yellow-500" />;
      default:
        return <Shirt className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading shop items</h3>
        <p className="text-muted-foreground">{(error as Error).message}</p>
        <Button variant="outline" className="mt-4" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Shop Items</h2>
        <Button onClick={() => {
          // Reset any parent-specific state if needed before opening add dialog
          // resetForm(); // Might not be needed if dialog handles its state
          setIsAddDialogOpen(true);
        }}>
          <PlusCircle className="h-4 w-4 mr-2" />
          Add New Item
        </Button>
      </div>

      <Table>
        <TableCaption>A list of all shop items in the system.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Item</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Stock</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Manager Mode</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!shopItems || shopItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center">
                No shop items found. Add your first item to get started.
              </TableCell>
            </TableRow>
          ) : (
            shopItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.id}</TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {getItemTypeIcon(item.itemType)}
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                        {item.description}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="capitalize">{item.itemType}</span>
                  <div className="text-xs text-muted-foreground">
                    {item.category || "decoration"}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {item.currencyType === "premium" ? (
                      <Gem className="h-4 w-4 mr-1 text-purple-500" />
                    ) : (
                      <Coins className="h-4 w-4 mr-1 text-emerald-500" />
                    )}
                    {Math.floor(parseFloat(item.price.toString()))} {item.currencyType === "premium" ? "Gems" : "Coins"}
                  </div>
                </TableCell>
                <TableCell>
                  {item.stock === null ? (
                    <span className="text-muted-foreground">Unlimited</span>
                  ) : (
                    <span
                      className={`${
                        item.stock <= 0
                          ? "text-red-500"
                          : item.stock < 5
                            ? "text-amber-500"
                            : "text-green-500"
                      }`}
                    >
                      {item.stock}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {item.isActive ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                      <X className="h-3 w-3 mr-1" />
                      Inactive
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {item.managerMode ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                      <Check className="h-3 w-3 mr-1" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                      <X className="h-3 w-3 mr-1" />
                      No
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openEditDialog(item)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Shop Item</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{item.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(item.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteItemMutation.isPending}
                          >
                            {deleteItemMutation.isPending ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Deleting...
                              </>
                            ) : (
                              "Delete"
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ShopItemDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleAddSubmit} // Pass the handler directly
        mode="add"
        isLoading={addItemMutation.isPending}
      />

      {selectedItem && (
        <ShopItemDialog
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleEditSubmit} // Pass the handler directly
          initialData={selectedItem}
          mode="edit"
          isLoading={updateItemMutation.isPending}
        />
      )}
    </div>
  );
}
